/* ScrollableComponent */
/* eslint-disable @typescript-eslint/no-unsafe-declaration-merging, no-magic-numbers, @typescript-eslint/no-explicit-any -- disabled to allow to inherit from AbstractComponent */

//ResizeObserver
import { ResizeObserver as ResizeObserverPolyfill } from '@juggle/resize-observer';
const ResizeObserver = window.ResizeObserver || ResizeObserverPolyfill;

import scrollSnapPolyfill from 'css-scroll-snap-polyfill';

scrollSnapPolyfill();

//smoothscroll
import smoothscroll from 'smoothscroll-polyfill';

smoothscroll.polyfill();

import {
  h, // eslint-disable-line @typescript-eslint/no-unused-vars
  Component,
  Element,
  Method,
  Prop,
  Watch,
  Listen,
  Event,
  State,
  EventEmitter,
  forceUpdate,
} from '@stencil/core';
import ScrollableTemplate from './cwc-scrollable.template';
import { AbstractComponent } from '@cewe-designsystem/abstract';
import { AspectRatio } from './aspect-ratio';
import UtilBrowserDetection from '@cewe-designsystem/util_browser-detection';

const orientations = ['vertical', 'horizontal'];
const sizes = {
  vertical: 'height',
  horizontal: 'width',
};
const spacings = {
  vertical: 'top',
  horizontal: 'left',
};
const dimensions = {
  vertical: ['top', 'bottom'],
  horizontal: ['left', 'right'],
};
const scrollSizes = {
  vertical: 'scrollHeight',
  horizontal: 'scrollWidth',
};
const scrollSpacings = {
  vertical: 'scrollTop',
  horizontal: 'scrollLeft',
};
const overflows = {
  vertical: 'overflow-y',
  horizontal: 'overflow-x',
};
const clients = {
  vertical: 'clientY',
  horizontal: 'clientX',
};
const clientSizes = {
  vertical: 'clientHeight',
  horizontal: 'clientWidth',
};
const pages = {
  vertical: 'pageY',
  horizontal: 'pageX',
};

@Component({
  tag: 'cwc-scrollable',
  styleUrl: './cwc-scrollable.scss',
  shadow: true,
})
export class ScrollableComponent {
  /**
   * properties
   */
  @Prop({ reflect: true }) scrollbarVisibility: string;
  @Prop({ reflect: true }) verticalScrollbarPosition: string;
  @Prop({ reflect: true }) horizontalScrollbarPosition: string;
  @Prop({ reflect: true }) forceAspectRatio: number;
  @Prop({ reflect: true }) aspectRatioLimit: number;
  @Prop({ reflect: true }) loop: boolean = false;

  /**
   * element
   */
  @Element() element: HTMLElement;
  @State() slottedElements: any;

  viewport: HTMLElement;
  content: any;
  shadowRoot: any;
  isScrolling: any;
  updatesIndex: boolean = false;
  lastPrev: number = 0;
  lastNext: number = 0;
  setIndexByFunction: boolean = false;
  allowResize: boolean = true;
  resizeObserver: any;
  lastWidth: number = 0;

  elements: any = {
    vertical: {
      scrollbar: null,
      scrollbarTrack: null,
      scrollbarThumb: null,
    },
    horizontal: {
      scrollbar: null,
      scrollbarTrack: null,
      scrollbarThumb: null,
    },
  };

  boundingBoxes: any = {
    viewport: null,
    vertical: {
      scrollbarTrack: null,
    },
    horizontal: {
      scrollbarTrack: null,
    },
  };

  restrictContentSize: any = {
    vertical: false,
    horizontal: false,
  };

  sizes: any = {
    vertical: {
      viewport: 0,
      scroll: 0,
      scrollbarTrack: 0,
      activeIndex: 0,
      viewportCenter: 0,
    },
    horizontal: {
      viewport: 0,
      scroll: 0,
      scrollbarTrack: 0,
      activeIndex: 0,
      viewportCenter: 0,
    },
  };

  ratios = {
    vertical: 1,
    horizontal: 1,
  };

  isScrollingWithThumb = {
    vertical: false,
    horizontal: false,
  };

  scrollingWithThumbOrigin = {
    pageX: 0,
    pageY: 0,
    scrollTop: 0,
    scrollLeft: 0,
  };

  animationFrame = null;
  aspectRatio;

  /**
   * Event thrown if the scrollable is scrolled
   *
   * @docs
   * @event on:scrollable:scroll
   */
  @Event({
    eventName: 'on:scrollable:scroll',
  })
  onScrollableScroll: EventEmitter;

  /**
   * Event thrown if the scrollable is scroll start
   *
   * @docs
   * @event on:scrollable:scroll:start
   */
  @Event({
    eventName: 'on:scrollable:scroll:start',
  })
  onScrollableScrollStart: EventEmitter;

  /**
   * Event thrown if the scrollable is scroll end
   *
   * @docs
   * @event on:scrollable:scroll:end
   */
  @Event({
    eventName: 'on:scrollable:scroll:end',
  })
  onScrollableScrollEnd: EventEmitter;

  /**
   * Event thrown if the scrollable is resized
   *
   * @docs
   * @event on:scrollable:resize
   */
  @Event({
    eventName: 'on:scrollable:resize',
  })
  onScrollableResize: EventEmitter;

  /**
   * events
   */
  @Listen('mousemove', { target: 'window' })
  handleScrollingWithThumb(event: MouseEvent) {
    for (const orientation of orientations) {
      if (this.isScrollingWithThumb[orientation]) {
        const scrollbarThumbOffset =
          // @ts-expect-error migrated from ts-ignore
          (event.touches ? event.touches[0][pages[orientation]] : event[pages[orientation]]) -
          this.scrollingWithThumbOrigin[pages[orientation]];
        this.viewport.scrollTo({
          left:
            this.scrollingWithThumbOrigin[scrollSpacings[orientation]] +
            scrollbarThumbOffset * this.ratios[orientation],
          behavior: 'smooth',
        });
        break;
      }
    }
  }

  @Listen('mouseup', { target: 'window' })
  handleScrollingWithThumbEnd() {
    for (const orientation of orientations) {
      if (this.isScrollingWithThumb[orientation]) {
        document.body.style.removeProperty('pointer-events');

        this.isScrollingWithThumb[orientation] = false;
        this.viewport.removeAttribute(`scrolling-with-${orientation}-thumb`);
        this.elements[orientation].scrollbar.removeAttribute('scrolling-with-thumb');
        // Gives back the focus to the viewport after clicking the scrollbar's thumb,
        // so we can continue to scroll using the keyboard (arrows, page down, page up, ...)
        this.viewport.focus();
      }
    }
  }

  /**
   * handleViewportScroll
   */
  handleViewportScroll() {
    if (this.animationFrame) {
      return;
    }

    (async () => {
      // emit scroll start event
      this.onScrollableScrollStart.emit(await this.getEmitData());

      this.animationFrame = requestAnimationFrame(async () => {
        // Clear our timeout throughout the scroll
        if (this.isScrolling) {
          window.clearTimeout(this.isScrolling);
          // emit scroll event
          this.onScrollableScroll.emit(await this.getEmitData());
        }

        this.animationFrame = null;
        this.updateScrollbarThumbPositions();

        // Set a timeout to run after scrolling ends
        this.isScrolling = setTimeout(async () => {
          await this.updateLoop();
          this.updateActiveIndex();

          // emit scroll end event
          this.onScrollableScrollEnd.emit(await this.getEmitData());
        }, 100);
      });
    })();
  }

  /**
   * handleViewportTouchStart
   */
  handleViewportTouchStart() {
    this.viewport.setAttribute('touch', '');
  }

  /**
   * handleViewportTouchEnd
   */
  handleViewportTouchEnd() {
    this.viewport.removeAttribute('touch');
  }

  /**
   * handleScrollbarThumbMouseDown
   * @param orientation
   * @param event
   */
  handleScrollbarThumbMouseDown(orientation = 'horizontal', event) {
    event.stopPropagation();
    document.body.style.setProperty('pointer-events', 'none');

    this.isScrollingWithThumb[orientation] = true;
    this.viewport.setAttribute(`scrolling-with-${orientation}-thumb`, '');
    this.elements[orientation].scrollbar.setAttribute('scrolling-with-thumb', '');

    for (const orientation of orientations) {
      this.scrollingWithThumbOrigin[pages[orientation]] = event.touches
        ? event.touches[0][pages[orientation]]
        : event[pages[orientation]];
      this.scrollingWithThumbOrigin[scrollSpacings[orientation]] = this.viewport[scrollSpacings[orientation]];
    }
  }

  /**
   * handleScrollbarTrackMouseDown
   * @param orientation
   * @param event
   */
  handleScrollbarTrackMouseDown(orientation, event) {
    this.boundingBoxes[orientation].scrollbarTrack = this.elements[orientation].scrollbarTrack.getBoundingClientRect();
    this.viewport.scrollTo({
      [spacings[orientation]]:
        (event[clients[orientation]] -
          this.boundingBoxes[orientation].scrollbarTrack[spacings[orientation]] -
          this.sizes[orientation].scrollbarThumb / 2) *
        this.ratios[orientation],
      behavior: 'smooth',
    });
  }

  /**
   * handleScrollbarClick
   */
  handleScrollbarClick() {
    this.viewport.focus();
  }

  /**
   * getEmitData
   */
  @Method()
  async getEmitData() {
    return {
      viewport: this.viewport.getBoundingClientRect(),
      content: this.content.getBoundingClientRect(),
    };
  }

  /**
   * watcher
   */
  @Watch('scrollbarVisibility')
  changedScrollbarVisibility(newValue = this.scrollbarVisibility) {
    if (newValue === 'always') {
      this.viewport.setAttribute('scrollbar-visible', '');
    } else {
      this.viewport.removeAttribute('scrollbar-visible');
    }
  }

  @Watch('verticalScrollbarPosition')
  changedVerticalScrollbarPosition(newValue = this.verticalScrollbarPosition) {
    if (newValue === 'left') {
      this.elements.vertical.scrollbar.setAttribute('left-position', '');
    } else {
      this.elements.vertical.scrollbar.removeAttribute('left-position');
    }
  }

  @Watch('horizontalScrollbarPosition')
  changedHorizontalScrollbarPosition(newValue = this.horizontalScrollbarPosition) {
    if (newValue === 'top') {
      this.elements.horizontal.scrollbar.setAttribute('top-position', '');
    } else {
      this.elements.horizontal.scrollbar.removeAttribute('top-position');
    }
  }

  /**
   * Called once just after the component fully loaded and the first render() occurs.
   */
  async componentDidRender() {
    if (this.viewport) return;

    this.viewport = this.element.shadowRoot.querySelector('cwc-scrollable-viewport');
    this.content = this.viewport.querySelector('cwc-scrollable-content');

    for (const orientation of orientations) {
      this.elements[orientation].scrollbar = this.element.shadowRoot.querySelector(
        `cwc-scrollable-bar[${orientation}-scrollbar]`,
      );
      this.elements[orientation].scrollbarTrack =
        this.elements[orientation].scrollbar.querySelector('cwc-scrollable-track');
      this.elements[orientation].scrollbarThumb =
        this.elements[orientation].scrollbarTrack.querySelector('cwc-scrollable-thumb');

      // Scroll to mouse position in scrollbar's track
      this.elements[orientation].scrollbarTrack.addEventListener(
        'mousedown',
        (event) => this.handleScrollbarTrackMouseDown(orientation, event),
        { passive: true },
      );

      // Gives back the focus to the viewport after clicking the scrollbar,
      // so we can continue to scroll using the keyboard (arrows, page down, page up, ...)
      this.elements[orientation].scrollbar.addEventListener('click', () => this.handleScrollbarClick(), {
        passive: true,
      });

      // Start of scrolling with thumb
      this.elements[orientation].scrollbarThumb.addEventListener(
        'mousedown',
        (event) => this.handleScrollbarThumbMouseDown(orientation, event),
        { passive: true },
      );
    }

    // Display scrollbars when scrolling with touch gestures
    this.viewport.addEventListener('touchstart', () => this.handleViewportTouchStart(), { passive: true });

    // Hide scrollbars when scrolling with touch gestures
    this.viewport.addEventListener('touchend', () => this.handleViewportTouchEnd(), { passive: true });

    // Update scrollbar's thumb position when scrolling
    this.viewport.addEventListener('scroll', () => this.handleViewportScroll(), { passive: true });

    this.updateCache();
    this.updateScrollbarThumbSizes();
    this.updateScrollbarThumbPositions();
    this.changedScrollbarVisibility();
    this.changedHorizontalScrollbarPosition();
    this.changedScrollbarVisibility();

    if (this.forceAspectRatio) {
      this.initResizeObserver();
      this.updateContentSize();

      this.aspectRatio = new AspectRatio(this.element, this.forceAspectRatio);

      if (this.aspectRatioLimit) {
        this.aspectRatio.setLimit(this.aspectRatioLimit);
      }

      await this.aspectRatio.handle(this.slottedElements, this.viewport);
    }

    if (!this.aspectRatio) {
      setTimeout(() => {
        let clientWidth = 0;
        // @ts-expect-error migrated from ts-ignore
        if (this.element.shadowRoot.host.getRootNode() && this.element.shadowRoot.host.getRootNode().host) {
          // @ts-expect-error migrated from ts-ignore
          clientWidth = this.element.shadowRoot.host.getRootNode().host.clientWidth;
        } else {
          // @ts-expect-error migrated from ts-ignore
          clientWidth = this.element.shadowRoot.host.getRootNode().clientWidth;
        }

        this.content.style.setProperty(`--viewport-width`, `${this.slottedElements.length * clientWidth}px`);
      }, 250);
      // fix firefox and safari resize problem
      window.addEventListener('resize', () => this.updateContentSize(), { passive: true });
    }

    this.updateActiveIndex();
  }

  /**
   * Called before every render().
   * A promise can be returned, that can be used to wait for the upcoming render.
   */
  componentWillRender() {
    if (!this.slottedElements) {
      this.slottedElements = this.getAssignedElements();
    }
  }

  /**
   * Called before every render().
   * A promise can be returned, that can be used to wait for the upcoming render.
   */
  componentWillUpdate() {
    if (this.slottedElements) {
      this.manageSlottedElements(this.slottedElements);
    }
  }

  /**
   * manageSlottedElements
   * @param elements
   */
  manageSlottedElements(elements) {
    const slot = elements[0].parentElement;

    if (elements) {
      elements.map((el: HTMLElement) => {
        el.remove();
      });
    }

    this.slottedElements.map((el) => {
      el.removeAttribute('animated');
      slot.appendChild(el);
    });
  }

  /**
   * initResizeObserver
   */
  initResizeObserver() {
    // Update entire scrollbar when resizing the viewport or the content
    if (!this.resizeObserver) {
      this.resizeObserver = new ResizeObserver(async () => {
        // check if lazy
        this.updateCache();

        if (this.animationFrame) {
          return;
        }

        this.animationFrame = requestAnimationFrame(() => {
          this.animationFrame = null;

          if (this.forceAspectRatio) {
            this.updateContentSize();
          }
          this.updateScrollbarThumbSizes();
          this.updateScrollbarThumbPositions();
        });

        this.onScrollableResize.emit(await this.getEmitData());
      });

      this.resizeObserver.observe(this.viewport);
      this.resizeObserver.observe(this.content);

      for (const orientation of orientations) {
        this.resizeObserver.observe(this.elements[orientation].scrollbarTrack);
      }
    }
  }

  /**
   * updateLoop
   */
  async updateLoop() {
    if (this.loop) {
      const scrollWidth = this.viewport.scrollWidth,
        scrollLeft = this.viewport.scrollLeft,
        width = this.viewport.offsetWidth;

      if (scrollLeft <= width * 0.8) {
        const prev = this.slottedElements.pop(),
          _elements = this.slottedElements;

        this.slottedElements = [];
        this.slottedElements.push(prev);
        _elements.map((el) => this.slottedElements.push(el));
      }

      if (scrollWidth - scrollLeft <= width) {
        const next = this.slottedElements.shift();
        this.slottedElements.push(next);
      }

      await forceUpdate(this);
    }
  }

  /**
   * updateActiveIndex
   */
  updateActiveIndex() {
    const elements = this.slottedElements;

    for (const orientation of orientations) {
      //eslint-disable-next-line @typescript-eslint/no-unused-vars
      for (const [index, element] of elements.entries()) {
        const _element = element.getBoundingClientRect(),
          elementLeftEnd = _element[dimensions[orientation][0]],
          elementRightEnd = _element[dimensions[orientation][1]];

        const _viewport = this.viewport.getBoundingClientRect(),
          viewportLeft = _viewport.left,
          viewportRight = _viewport.right,
          viewportCenter = (viewportRight - viewportLeft) / 2;

        if (elementLeftEnd < viewportCenter && elementRightEnd > viewportCenter) {
          this.sizes[orientation].activeIndex = Number.parseInt(element.getAttribute('index'));
          this.lastPrev = this.sizes[orientation].activeIndex - 1;
          this.lastNext = this.sizes[orientation].activeIndex + 1;
        }
      }
    }
  }

  /**
   * updateCache
   */
  updateCache() {
    // Caches as much as possible to avoid useless repaint/reflow
    const computedStyle = window.getComputedStyle(this.viewport);
    this.boundingBoxes.viewport = this.viewport.getBoundingClientRect();

    for (const orientation of orientations) {
      this.restrictContentSize[orientation] =
        computedStyle.getPropertyValue(`--viewport-${overflows[orientation]}`).trim() === 'hidden';
      this.boundingBoxes[orientation].scrollbarTrack =
        this.elements[orientation].scrollbarTrack.getBoundingClientRect();
      this.sizes[orientation].viewport = Math.floor(this.boundingBoxes.viewport[sizes[orientation]] * 10) / 10;
      this.sizes[orientation].scroll = this.viewport[scrollSizes[orientation]];
      this.sizes[orientation].scrollbarTrack =
        Math.floor(this.boundingBoxes[orientation].scrollbarTrack[sizes[orientation]] * 10) / 10;
      this.ratios[orientation] = this.sizes[orientation].scroll / this.sizes[orientation].scrollbarTrack;
      this.sizes[orientation].viewportCenter = this.viewport[clientSizes[orientation]] / 2;
      /*
          This VooDoo Number is here to force stop the resizing loop that gets triggered at some screen sizes
          this forces it to stay larger than the images and not bounce between trigger states.
       */

      if (this.sizes[orientation].viewport < 400) {
        this.sizes[orientation].viewport = 400;
      }
    }
  }

  /**
   * updateContentSize
   */
  updateContentSize() {
    for (const orientation of orientations) {
      if (this.aspectRatio) {
        // @ts-expect-error migrated from ts-ignore
        this.element.shadowRoot.host.style.setProperty(
          `--viewport-${sizes[orientation]}`,
          `${this.sizes[orientation].viewport}px`,
        );

        if (this.restrictContentSize[orientation]) {
          this.content.style.setProperty(
            sizes[orientation],
            `var(--aspect-ratio-viewport-${sizes[orientation]}-forced, var(--viewport-${sizes[orientation]}))`,
          );
        } else {
          this.content.style.removeProperty(sizes[orientation]);
        }
      } else {
        this.content.style.setProperty(
          `--viewport-width`,
          `${this.slottedElements.length * this.element.clientWidth}px`,
        );
      }
    }
  }

  /**
   * updateScrollbarThumbSizes
   */
  updateScrollbarThumbSizes() {
    for (const orientation of orientations) {
      if (this.sizes[orientation].scroll <= Math.ceil(this.sizes[orientation].viewport)) {
        this.elements[orientation].scrollbar.setAttribute('hidden', '');
        this.sizes[orientation].scrollbarThumb = this.sizes[orientation].scrollbarTrack;
      } else {
        this.elements[orientation].scrollbar.removeAttribute('hidden');
        this.sizes[orientation].scrollbarThumb = this.sizes[orientation].viewport / this.ratios[orientation];
      }

      this.elements[orientation].scrollbarThumb.style[sizes[orientation]] =
        `${this.sizes[orientation].scrollbarThumb}px`;
    }
  }

  /**
   * updateScrollbarThumbPositions
   */
  updateScrollbarThumbPositions() {
    for (const orientation of orientations) {
      const scrollbarThumbOffset = {
        vertical: 0,
        horizontal: 0,
      };

      if (this.sizes[orientation].scroll <= Math.ceil(this.sizes[orientation].viewport)) {
        this.elements[orientation].scrollbar.setAttribute('hidden', '');
        scrollbarThumbOffset[orientation] = 0;
      } else {
        this.elements[orientation].scrollbar.removeAttribute('hidden');
        scrollbarThumbOffset[orientation] = this.viewport[scrollSpacings[orientation]] / this.ratios[orientation];
      }

      this.elements[orientation].scrollbarThumb.style.transform =
        `translate3D(${scrollbarThumbOffset.horizontal}px, ${scrollbarThumbOffset.vertical}px, 0)`;
    }
  }

  /**
   * moveBy
   * @param x
   * @param y
   */
  @Method()
  async moveBy(x: number, y: number) {
    this.viewport.scrollBy({
      left: x,
      top: y,
      behavior: 'smooth',
    });
  }

  /**
   * moveTo
   * @param x
   * @param y
   */
  @Method()
  async moveTo(x: number, y: number) {
    this.viewport.scrollTo({
      left: x,
      top: y,
      behavior: 'smooth',
    });
  }

  /**
   * moveNext
   */
  @Method()
  async scrollToNext(orientation = 'horizontal') {
    const elements = this.slottedElements,
      nextIndex = this.getNextIndex(orientation);

    const elementLeftOffset =
      elements[nextIndex].offsetLeft - (this.viewport.clientWidth - elements[nextIndex].clientWidth) / 2;

    this.isScrolling = true;

    const utilBrowserDetection = new UtilBrowserDetection();
    const browser = utilBrowserDetection.getBrowser();
    let type;
    if (browser == 'safari') {
      type = this.viewport.style.scrollSnapType;
      this.viewport.style.scrollSnapType = 'none';
    }

    this.viewport.scrollTo({
      [spacings[orientation]]: elementLeftOffset,
      behavior: 'smooth',
    });

    if (browser == 'safari') {
      setTimeout(() => {
        // @ts-expect-error migrated from ts-ignore
        this.viewport.scrollSnapType = type;
        this.viewport.scrollTo({
          [spacings[orientation]]: elementLeftOffset,
        });
      }, 500);
    }

    this.lastNext = this.sizes[orientation].activeIndex;
  }

  /**
   * getElements
   */
  @Method()
  async getElements(): Promise<any> {
    return this.slottedElements;
  }

  /**
   * getElementsLength
   */
  @Method()
  async getElementsLength(): Promise<any> {
    const elements = this.slottedElements;

    return elements.length;
  }

  /**
   * getLastElement
   */
  @Method()
  async getLastElement(): Promise<any> {
    const elements = this.slottedElements;

    return elements[elements.length - 1];
  }

  /**
   * getFirstElement
   */
  @Method()
  async getFirstElement(): Promise<any> {
    const elements = this.slottedElements;

    return elements[0];
  }

  /**
   * getActiveElement
   */
  @Method()
  async getActiveElement(): Promise<any> {
    const elements = this.slottedElements,
      activeIndex = await this.getActiveIndex();

    return elements.filter((el) => activeIndex == el.getAttribute('index'))[0];
  }

  /**
   * getActiveIndex
   */
  @Method()
  async getActiveIndex(orientation = 'horizontal') {
    return this.sizes[orientation].activeIndex;
  }

  /**
   * setActiveIndex
   */
  @Method()
  async setActiveIndex(index: number, orientation = 'horizontal') {
    this.sizes[orientation].activeIndex = index;
  }

  /**
   * getNextIndex
   * @param orientation
   * @private
   */
  protected getNextIndex(orientation, updateActiveIndex = true) {
    const elements = this.slottedElements,
      activeIndex = this.sizes[orientation].activeIndex;
    let nextIndex = 0;

    elements.map((el, i) => {
      if (activeIndex == el.getAttribute('index')) {
        nextIndex = i < elements.length - 1 ? i + 1 : 0;
        if (updateActiveIndex) {
          this.sizes[orientation].activeIndex = elements[nextIndex].getAttribute('index');
        }
      }
    });

    return nextIndex;
  }

  @Method()
  async predictNextIndex(orientation = 'horizontal') {
    const index = this.getNextIndex(orientation, false);

    return this.slottedElements[index].getAttribute('index');
  }

  /**
   * movePrev
   */
  @Method()
  async scrollToPrev(orientation = 'horizontal') {
    const elements = this.slottedElements,
      prevIndex = this.getPrevIndex(orientation);

    const elementLeftOffset =
      elements[prevIndex].offsetLeft - (this.viewport.clientWidth - elements[prevIndex].clientWidth) / 2;

    this.isScrolling = true;

    const utilBrowserDetection = new UtilBrowserDetection();
    const browser = utilBrowserDetection.getBrowser();
    let type;
    if (browser == 'safari') {
      type = this.viewport.style.scrollSnapType;
      this.viewport.style.scrollSnapType = 'none';
    }

    this.viewport.scrollTo({
      [spacings[orientation]]: elementLeftOffset,
      behavior: 'smooth',
    });

    if (browser == 'safari') {
      setTimeout(() => {
        // @ts-expect-error migrated from ts-ignore
        this.viewport.scrollSnapType = type;
        this.viewport.scrollTo({
          [spacings[orientation]]: elementLeftOffset,
        });
      }, 500);
    }

    this.lastPrev = this.sizes[orientation].activeIndex;
  }

  /**
   * getPrevIndex
   * @param orientation
   * @private
   */
  private getPrevIndex(orientation, updateActiveIndex = true) {
    const elements = this.slottedElements,
      activeIndex = this.sizes[orientation].activeIndex;
    let prevIndex = 0;

    elements.map((el, i) => {
      if (activeIndex == el.getAttribute('index')) {
        prevIndex = i > 0 ? i - 1 : elements.length - 1;
        if (updateActiveIndex) {
          this.sizes[orientation].activeIndex = elements[prevIndex].getAttribute('index');
        }
      }
    });

    return prevIndex;
  }

  @Method()
  async predictPrevIndex(orientation = 'horizontal') {
    const index = this.getPrevIndex(orientation, false);

    return this.slottedElements[index].getAttribute('index');
  }

  /**
   * getContentSlot
   */
  getContentSlot() {
    return this.element.querySelector('slot');
  }

  /**
   * getAssignedElements
   * @private
   */
  private getAssignedElements() {
    let children;
    if (this.element.shadowRoot.host.getRootNode().nodeName == '#document') {
      children = Array.from(this.element.shadowRoot.host.children);
    }

    // @ts-expect-error migrated from ts-ignore
    children = Array.from(this.element.shadowRoot.host.getRootNode().host.children);

    // add index
    children.map((el: HTMLElement, index) => {
      if (!el.getAttribute('index')) {
        el.setAttribute('index', String(index));
      }

      el.setAttribute('animated', '');
    });

    return children;
  }

  /**
   * getAssignedNodes
   */
  @Method()
  async getAssignedNodes() {
    return this.getContentSlot() ? this.getContentSlot().assignedNodes() : Array.from(this.element.childNodes);
  }

  /**
   * render
   */
  render() {
    return <ScrollableTemplate controller={this} />;
  }
}

/**
 * mixin abstract functions
 */
Object.setPrototypeOf(ScrollableComponent.prototype, AbstractComponent.prototype);

export interface ScrollableComponent extends AbstractComponent {}
